import { useState, useRef } from 'react';
import { bytesToBlob } from '../../crypto/crypto';
import { prepareImageUpload } from '../../crypto/uploadImage';
import { extractVideoPosterFrame } from '../../crypto/videoPoster';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useBabyMilestones, useUploadPhotos } from '../../data/queries';
import { useFileDrop } from '../../hooks/useFileDrop';
import { Sheet } from '../shared/Sheet';
import { Icon } from '../chrome/Icon';
import { useT } from '../../i18n';
import { todayIso, isoForDateChoice } from '../../data/derive';
import type { Baby } from '../../data/types';

interface Props {
  open: boolean;
  onClose: () => void;
  baby: Baby;
}

/** Matches the backend's per-file caps (MediaController::MAX_IMAGE_BYTES / MAX_VIDEO_BYTES). */
const MAX_IMAGE_BYTES = 30 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

const DATE_OPTIONS = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thisweek',  label: 'This week' },
  { id: 'custom',    label: 'Pick a date' },
];

const GROUP_KEY_LABELS: Record<string, string> = {
  // Existing snake_case keys (for safety)
  motor_gross:      'Motor – Gross',
  motor_fine:       'Motor – Fine',
  communication:    'Communication',
  social_emotional: 'Social',
  cognitive:        'Cognitive',

  // DB capitalized keys
  'Motor - Gross':      'Motor – Gross',
  'Motor - Fine':       'Motor – Fine',
  'Communication':      'Communication',
  'Social & Emotional': 'Social',
  'Cognitive':          'Cognitive',
  'Boundary Stone':     'Boundary Stone',
  'Language':           'Language',
  'Nutrition':          'Nutrition',
  'Self-Care':          'Self-Care',
  'Sleep':              'Sleep',
  'Teeth':              'Teeth',
};

export function PhotoUploadSheet({ open, onClose, baby }: Props) {
  const t = useT();
  const enc = useEncryption();
  const milestonesQ = useBabyMilestones(baby.id ?? null);
  const uploadPhotos = useUploadPhotos();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<{ id: string; file: File; src: string; type: 'image' | 'video' }[]>([]);
  const [uploadType, setUploadType] = useState<'moment' | 'milestone'>('moment');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [dateChoice, setDateChoice] = useState('today');
  const [customDate, setCustomDate] = useState(todayIso());

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Group milestones by category for the dropdown
  const groupedMilestones = (() => {
    const list = milestonesQ.data?.milestones ?? [];
    const groups: Record<string, typeof list> = {};
    for (const m of list) {
      const g = m.group_key;
      if (!groups[g]) groups[g] = [];
      groups[g].push(m);
    }
    return groups;
  })();

  const addFiles = (files: File[]) => {
    if (!files.length) return;
    const next = [...selectedFiles];
    for (const f of files) {
      if (next.length >= 12) break; // Limit to 12 files
      const type = f.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|ogv|avi|mkv|3gp)$/i.test(f.name) ? 'video' : 'image';
      const src = URL.createObjectURL(f);
      next.push({
        id: `up-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file: f,
        src,
        type,
      });
    }
    setSelectedFiles(next);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    addFiles(files);
    e.target.value = '';
  };

  const { isDraggingOver, dropProps } = useFileDrop({
    onDropFiles: addFiles,
    disabled: uploading || selectedFiles.length >= 12,
  });

  const removeFile = (id: string) => {
    const item = selectedFiles.find((f) => f.id === id);
    if (item?.src.startsWith('blob:')) {
      URL.revokeObjectURL(item.src);
    }
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setError(null);
    const ready = await enc.ensureReady();
    if (!ready) return;

    setUploading(true);
    setProgress({ done: 0, total: selectedFiles.length });

    try {
      const form = new FormData();
      const chosenDate = isoForDateChoice(dateChoice, customDate);
      form.append('taken_date', chosenDate);

      if (uploadType === 'moment') {
        if (title.trim()) {
          form.append('title', title.trim());
        }
        if (description.trim()) {
          form.append('description', description.trim());
        }
      } else {
        if (!milestoneId) {
          throw new Error(t('upload.milestoneRequired', 'Please select a milestone.'));
        }
        form.append('milestone_id', milestoneId);
      }

      const isEncrypted = enc.isEncryptedMode;
      form.append('is_encrypted', isEncrypted ? '1' : '0');

      const metadata: { mime_type: string; width_px: number; height_px: number }[] = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const item = selectedFiles[i]!;
        const file = item.file;
        const isVideo = item.type === 'video';

        if (isVideo) {
          // Video isn't compressed client-side, so check the bytes that actually
          // get uploaded: the encrypted blob when E2EE, the raw file otherwise.
          if (isEncrypted) {
            const { bytes } = enc.encryptForUpload(new Uint8Array(await file.arrayBuffer()));
            if (bytes.length > MAX_VIDEO_BYTES) {
              throw new Error(t('upload.videoTooLarge', 'Video "{name}" exceeds the 50MB limit.').replace('{name}', file.name));
            }
            form.append(`file_${i}`, bytesToBlob(bytes, 'application/octet-stream'), `${file.name}.bin`);
          } else {
            if (file.size > MAX_VIDEO_BYTES) {
              throw new Error(t('upload.videoTooLarge', 'Video "{name}" exceeds the 50MB limit.').replace('{name}', file.name));
            }
            form.append(`file_${i}`, file, file.name);
          }
          // Poster-frame thumbnail: the server has no video decoder, so send
          // one grabbed client-side (encrypted only when the video itself is).
          const poster = await extractVideoPosterFrame(file);
          if (poster) {
            const posterBytes = new Uint8Array(await poster.arrayBuffer());
            const thumbBytes = isEncrypted ? enc.encryptForUpload(posterBytes).bytes : posterBytes;
            form.append(`thumbnail_${i}`, bytesToBlob(thumbBytes, 'application/octet-stream'), `${file.name}.thumb.bin`);
          }
          metadata.push({ mime_type: file.type || 'video/mp4', width_px: 0, height_px: 0 });
        } else {
          // Check the actual compressed(+encrypted) bytes, not the source file —
          // a large source photo can compress well under the limit, and vice
          // versa a small one could still exceed it once encrypted.
          const parts = await prepareImageUpload(file, enc.encryptForUpload);
          if (parts.fileBlob.size > MAX_IMAGE_BYTES) {
            throw new Error(t('upload.imageTooLarge', 'Photo "{name}" exceeds the 30MB limit.').replace('{name}', file.name));
          }
          form.append(`file_${i}`, parts.fileBlob, `${file.name}.bin`);
          if (parts.thumbBlob) form.append(`thumbnail_${i}`, parts.thumbBlob, `${file.name}.thumb.bin`);
          metadata.push({ mime_type: parts.mime, width_px: parts.width, height_px: parts.height });
        }

        setProgress({ done: i + 1, total: selectedFiles.length });
      }

      form.append('metadata', JSON.stringify(metadata));

      await uploadPhotos.mutateAsync({ babyId: baby.id!, form });

      // Clean up object URLs
      for (const item of selectedFiles) {
        if (item.src.startsWith('blob:')) URL.revokeObjectURL(item.src);
      }

      // Reset
      setSelectedFiles([]);
      setTitle('');
      setDescription('');
      setMilestoneId('');
      setDateChoice('today');
      setCustomDate(todayIso());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('upload.failed', 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} size="md" maxHeight="92dvh" label={t('upload.header', 'Add photos & videos')}>
      <div className="flex flex-col gap-6 px-6 pb-8 pt-0 overflow-y-auto">

        {/* Header line */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('upload.header', 'Add photos & videos')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        {/* Media Selector Grid */}
        <div>
          <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3 mb-2">
            {t('upload.selectedMedia', 'Selected media ({n}/12)').replace('{n}', String(selectedFiles.length))}
          </span>
          <div
            {...dropProps}
            className={[
              'grid grid-cols-4 gap-2 rounded-xl p-1.5 -m-1.5 transition-all duration-150',
              isDraggingOver ? 'bg-accent/10 ring-2 ring-accent ring-dashed' : '',
            ].join(' ')}
          >
            {selectedFiles.map((f) => (
              <div key={f.id} className="relative aspect-square w-full overflow-hidden rounded-lg bg-surface border border-line">
                {f.type === 'video' ? (
                  <video src={f.src} muted className="size-full object-cover" />
                ) : (
                  <img src={f.src} alt="" className="size-full object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeFile(f.id)}
                  title={t('upload.remove', 'Remove')}
                  className="absolute -right-1.5 -top-1.5 flex size-5.5 items-center justify-center rounded-full bg-ink text-bg border-[1.5px] border-bg shadow-sm cursor-pointer"
                >
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M3 3l6 6M9 3L3 9" />
                  </svg>
                </button>
              </div>
            ))}
            {selectedFiles.length < 12 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={[
                  'flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg text-accent-ink cursor-pointer transition-all',
                  isDraggingOver ? 'scale-105' : '',
                ].join(' ')}
                style={{
                  border: isDraggingOver
                    ? '2px dashed var(--color-accent)'
                    : '1.5px dashed color-mix(in oklch, var(--color-accent) 35%, var(--color-line))',
                  background: isDraggingOver
                    ? 'color-mix(in oklch, var(--color-accent) 15%, var(--color-bg))'
                    : 'color-mix(in oklch, var(--color-accent) 5%, var(--color-bg))',
                }}
              >
                <Icon name="plus" size={20} />
                <span className="text-[9.5px] font-semibold uppercase tracking-wider">
                  {isDraggingOver ? t('upload.dropHere', 'Drop') : t('upload.add', 'Add')}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Segmented Control Upload Type */}
        <div>
          <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3 mb-2">
            {t('upload.typeOfMoments', 'Type of moments')}
          </span>
          <div className="flex gap-2 bg-surface p-1 rounded-xl border border-line">
            <button
              onClick={() => setUploadType('moment')}
              className={[
                'flex-1 py-2 text-sm font-semibold rounded-lg transition-all',
                uploadType === 'moment' ? 'bg-bg text-accent-ink shadow-sm' : 'text-ink-2',
              ].join(' ')}
              style={uploadType === 'moment' ? { border: '1px solid var(--color-line)' } : undefined}
            >
              {t('upload.generalMoment', 'General Moment')}
            </button>
            <button
              onClick={() => setUploadType('milestone')}
              className={[
                'flex-1 py-2 text-sm font-semibold rounded-lg transition-all',
                uploadType === 'milestone' ? 'bg-bg text-accent-ink shadow-sm' : 'text-ink-2',
              ].join(' ')}
              style={uploadType === 'milestone' ? { border: '1px solid var(--color-line)' } : undefined}
            >
              {t('upload.milestoneAchieved', 'Milestone Achieved')}
            </button>
          </div>
        </div>

        {/* Dynamic Fields */}
        {uploadType === 'moment' ? (
          <div className="flex flex-col gap-4">
            <div>
              <label htmlFor="upload-title" className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                {t('upload.titleOptional', 'Title (optional)')}
              </label>
              <input
                id="upload-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('upload.titlePlaceholder', 'e.g. Fun day at the park')}
                className="mt-1.5 min-h-12 w-full rounded-xl border border-line bg-surface px-4 text-[14px] text-ink outline-none focus:border-accent"
              />
            </div>
            <div>
              <label htmlFor="upload-description" className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                {t('upload.descriptionOptional', 'Description (optional)')}
              </label>
              <textarea
                id="upload-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('upload.descriptionPlaceholder', 'Write something about this moment…')}
                rows={3}
                className="mt-1.5 w-full rounded-xl border border-line bg-surface p-4 text-[14px] text-ink outline-none focus:border-accent resize-y min-h-[80px]"
              />
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="upload-milestone" className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3">
              {t('upload.selectMilestone', 'Select milestone')}
            </label>
            <select
              id="upload-milestone"
              value={milestoneId}
              onChange={(e) => setMilestoneId(e.target.value)}
              className="mt-1.5 min-h-12 w-full rounded-xl border border-line bg-surface px-4 text-[14.5px] text-ink outline-none focus:border-accent"
            >
              <option value="">{t('upload.chooseMilestonePlaceholder', '-- Choose a milestone --')}</option>
              {Object.entries(groupedMilestones).map(([key, list]) => (
                <optgroup key={key} label={GROUP_KEY_LABELS[key] ?? key}>
                  {list.map((m) => (
                    <option key={m.milestone_id} value={m.milestone_id}>
                      {m.status === 'achieved' ? `✓ ${m.name}` : m.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        {/* Date Options */}
        <div>
          <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-3 mb-2">
            {t('upload.whenTaken', 'When was it taken?')}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {DATE_OPTIONS.map((d) => {
              const dOn = dateChoice === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setDateChoice(d.id)}
                  className={[
                    'inline-flex items-center gap-1.5 rounded-3xl px-3 py-1.5 text-[12.5px] transition-all',
                    dOn ? 'text-white font-semibold border-0' : 'border border-line bg-surface text-ink font-medium',
                  ].join(' ')}
                  style={dOn ? { background: 'var(--color-accent)' } : undefined}
                >
                  {d.id === 'custom' && <Icon name="calendar" size={13} color={dOn ? 'white' : 'var(--color-ink-2)'} />}
                  {t('status.when.' + d.id, d.label)}
                </button>
              );
            })}
          </div>

          {dateChoice === 'custom' && (
            <div className="mt-2.5">
              <input
                type="date"
                value={customDate || todayIso()}
                max={todayIso()}
                onChange={(e) => setCustomDate(e.target.value)}
                className="min-h-11 w-full rounded-xl border border-line bg-surface px-3.5 text-[14px] text-ink outline-none focus:border-accent"
              />
            </div>
          )}
        </div>

        {/* Encryption mode notice */}
        <div className="flex flex-wrap items-center gap-1.5 text-[11.5px] tracking-wide text-ink-3">
          {enc.isEncryptedMode ? (
            <span className="inline-flex items-center gap-1 font-semibold text-accent-ink">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              {t('upload.encryptedNotice', 'Will be end-to-end encrypted')}
            </span>
          ) : (
            <span>{t('upload.unencryptedNotice', 'Stored without encryption')}</span>
          )}
        </div>

        {/* Error message */}
        {error && <p className="text-[12.5px] text-[#7E3320] leading-snug">{error}</p>}

        {/* Uploading progress indicator */}
        {uploading && progress && (
          <div className="flex flex-col gap-1.5">
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-valuenow={progress.done}
              className="h-1 w-full overflow-hidden rounded-full bg-line"
            >
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-300"
                style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
              />
            </div>
            <p className="text-[13px] text-ink-2">
              {t(
                enc.isEncryptedMode ? 'upload.progressEncrypting' : 'upload.progressPlain',
                enc.isEncryptedMode
                  ? 'Uploading {done} of {total} · encrypting on your device…'
                  : 'Uploading {done} of {total}…',
              )
                .replace('{done}', String(progress.done + 1 > progress.total ? progress.total : progress.done + 1))
                .replace('{total}', String(progress.total))}
            </p>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploading}
          className="
            mt-2 inline-flex w-full min-h-14 items-center justify-center gap-2.5
            rounded-3xl bg-ink px-6 text-[15.5px] font-semibold text-bg
            disabled:opacity-50
          "
        >
          {uploading ? t('common.saving', 'Uploading…') : t('upload.cta', 'Upload moments')}
          {!uploading && <Icon name="check" size={18} color="currentColor" />}
        </button>

      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={onFileChange}
        className="hidden"
      />
    </Sheet>
  );
}
